import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Trash2, 
  Search,
  Plus,
  Shield,
  User,
  Lock,
  MoreVertical,
  Edit,
  Crown
} from "lucide-react";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { adminService } from "@/api/services/admin.service";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Admin, CreateAdminRequest, UpdateAdminRequest } from "@/api/types/admin.types";

const AdminUsers = () => {
  const { t, isRTL, language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  useNavigate(); // Required for page navigation context

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);

  // Form states
  const [formData, setFormData] = useState<CreateAdminRequest>({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    position: '',
    is_super_admin: false,
  });

  const [resetPasswordData, setResetPasswordData] = useState({
    password: '',
    password_confirmation: '',
  });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch admins
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'users', debouncedSearchQuery],
    queryFn: () => adminService.getAdmins({
      search: debouncedSearchQuery || undefined,
      limit: 100,
    }),
    enabled: user?.is_super_admin && !!user,
  });

  const admins = data?.data?.data || [];

  // Create admin mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateAdminRequest) => adminService.createAdmin(data),
    onSuccess: () => {
      toast({
        title: t('admin.users.createSuccess'),
        description: '',
      });
      setIsCreateDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.error?.message || error?.message,
        variant: 'destructive',
      });
    },
  });

  // Update admin mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateAdminRequest }) => 
      adminService.updateAdmin(id, data),
    onSuccess: () => {
      toast({
        title: t('admin.users.updateSuccess'),
        description: '',
      });
      setIsEditDialogOpen(false);
      setSelectedAdmin(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.error?.message || error?.message,
        variant: 'destructive',
      });
    },
  });

  // Delete admin mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminService.deleteAdmin(id),
    onSuccess: () => {
      toast({
        title: t('admin.users.deleteSuccess'),
        description: '',
      });
      setIsDeleteDialogOpen(false);
      setSelectedAdmin(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.error?.message || error?.message,
        variant: 'destructive',
      });
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { password: string; password_confirmation: string } }) => 
      adminService.resetAdminPassword(id, data),
    onSuccess: () => {
      toast({
        title: t('admin.users.passwordResetSuccess'),
        description: '',
      });
      setIsResetPasswordDialogOpen(false);
      setResetPasswordData({ password: '', password_confirmation: '' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.error?.message || error?.message,
        variant: 'destructive',
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
      position: '',
      is_super_admin: false,
    });
  };

  const handleCreate = () => {
    if (formData.password !== formData.password_confirmation) {
      toast({
        title: t('common.error'),
        description: t('auth.passwordsDoNotMatch'),
        variant: 'destructive',
      });
      return;
    }
    createMutation.mutate(formData);
  };

  const handleEdit = () => {
    if (!selectedAdmin) return;
    updateMutation.mutate({
      id: selectedAdmin.id,
      data: {
        name: formData.name,
        email: formData.email,
        position: formData.position,
        is_super_admin: formData.is_super_admin,
      },
    });
  };

  const handleDelete = () => {
    if (!selectedAdmin) return;
    deleteMutation.mutate(selectedAdmin.id);
  };

  const handleResetPassword = () => {
    if (!selectedAdmin) return;
    if (resetPasswordData.password !== resetPasswordData.password_confirmation) {
      toast({
        title: t('common.error'),
        description: t('auth.passwordsDoNotMatch'),
        variant: 'destructive',
      });
      return;
    }
    resetPasswordMutation.mutate({
      id: selectedAdmin.id,
      data: resetPasswordData,
    });
  };

  const openEditDialog = (admin: Admin) => {
    setSelectedAdmin(admin);
    setFormData({
      name: admin.user.name,
      email: admin.user.email,
      password: '',
      password_confirmation: '',
      position: admin.position || '',
      is_super_admin: admin.is_super_admin,
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (admin: Admin) => {
    setSelectedAdmin(admin);
    setIsDeleteDialogOpen(true);
  };

  const openResetPasswordDialog = (admin: Admin) => {
    setSelectedAdmin(admin);
    setResetPasswordData({ password: '', password_confirmation: '' });
    setIsResetPasswordDialogOpen(true);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'PPP', { locale: language === 'ar' ? ar : enUS });
    } catch {
      return dateString;
    }
  };

  if (!user?.is_super_admin) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-xl font-semibold mb-2">{t('admin.users.superAdminOnly')}</h2>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('admin.users.title')}</h1>
          <p className="text-muted-foreground">{t('admin.users.subtitle')}</p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          {t('admin.users.create')}
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
            <Input
              placeholder={t('admin.users.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`${isRTL ? 'pr-10' : 'pl-10'}`}
            />
          </div>
        </CardContent>
      </Card>

      {/* Admins Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground mb-4">{t('common.error')}</p>
              <Button onClick={() => refetch()}>{t('common.retry')}</Button>
            </div>
          ) : admins.length === 0 ? (
            <div className="p-8 text-center">
              <User className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">{t('admin.users.noUsers')}</h3>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admin.users.name')}</TableHead>
                  <TableHead>{t('admin.users.email')}</TableHead>
                  <TableHead>{t('admin.users.position')}</TableHead>
                  <TableHead>{t('admin.users.isSuperAdmin')}</TableHead>
                  <TableHead>{t('admin.users.createdAt')}</TableHead>
                  <TableHead className="text-right">{t('admin.users.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {admin.is_super_admin && (
                          <Crown className="w-4 h-4 text-yellow-500" />
                        )}
                        <span className="font-medium">{admin.user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{admin.user.email}</TableCell>
                    <TableCell>{admin.position || '-'}</TableCell>
                    <TableCell>
                      {admin.is_super_admin ? (
                        <Badge variant="default" className="bg-yellow-500">
                          <Shield className="w-3 h-3 mr-1" />
                          {t('admin.users.isSuperAdmin')}
                        </Badge>
                      ) : (
                        <Badge variant="outline">{t('admin.users.status')}</Badge>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(admin.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(admin)}>
                            <Edit className="w-4 h-4 mr-2" />
                            {t('admin.users.edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openResetPasswordDialog(admin)}>
                            <Lock className="w-4 h-4 mr-2" />
                            {t('admin.users.resetPassword')}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => openDeleteDialog(admin)}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            {t('admin.users.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin.users.create')}</DialogTitle>
            <DialogDescription>{t('admin.users.subtitle')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('admin.users.name')}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('auth.namePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.users.email')}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('auth.emailPlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.users.position')}</Label>
              <Input
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                placeholder={t('admin.users.positionPlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.users.password')}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={t('admin.users.passwordPlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.users.confirmPassword')}</Label>
              <Input
                type="password"
                value={formData.password_confirmation}
                onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                placeholder={t('admin.users.passwordPlaceholder')}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.is_super_admin}
                onCheckedChange={(checked) => setFormData({ ...formData, is_super_admin: checked })}
              />
              <Label>{t('admin.users.isSuperAdmin')}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? t('common.loading') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin.users.edit')}</DialogTitle>
            <DialogDescription>{t('admin.users.subtitle')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('admin.users.name')}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.users.email')}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.users.position')}</Label>
              <Input
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                placeholder={t('admin.users.positionPlaceholder')}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.is_super_admin}
                onCheckedChange={(checked) => setFormData({ ...formData, is_super_admin: checked })}
              />
              <Label>{t('admin.users.isSuperAdmin')}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleEdit} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin.users.delete')}</DialogTitle>
            <DialogDescription>
              {t('common.confirmDelete')}: {selectedAdmin?.user.name}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? t('common.loading') : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetPasswordDialogOpen} onOpenChange={setIsResetPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin.users.resetPassword')}</DialogTitle>
            <DialogDescription>
              {selectedAdmin?.user.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('admin.users.password')}</Label>
              <Input
                type="password"
                value={resetPasswordData.password}
                onChange={(e) => setResetPasswordData({ ...resetPasswordData, password: e.target.value })}
                placeholder={t('admin.users.passwordPlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.users.confirmPassword')}</Label>
              <Input
                type="password"
                value={resetPasswordData.password_confirmation}
                onChange={(e) => setResetPasswordData({ ...resetPasswordData, password_confirmation: e.target.value })}
                placeholder={t('admin.users.passwordPlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResetPasswordDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleResetPassword} disabled={resetPasswordMutation.isPending}>
              {resetPasswordMutation.isPending ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
